import React from 'react';
import styled from 'styled-components';
import { defaultColorPalette, Text, tombac } from 'tombac';
import { LimitWithUsage } from '../../experimental/navbar/LegolandNavbarLimits';
import { getProductName } from '../../shared/getProductName';
import { StyledLimits } from './StyledLimits';

const formatValue = (type: string, value: any) => {
  switch (type) {
    case 'GEO':
      return value.coordinates.length + ' region(s)';
    case 'COUNT':
      return value;
    case 'SQUARE_KM':
      return Number(Number(value).toFixed(1)) + ' km²';
    case 'KM':
      return Number(Number(value).toFixed(1)) + ' km';
    case 'DATE_RANGE':
      if (value.from === undefined && value.to === undefined) {
        return `None`;
      }
      if (value.from === undefined && value.to !== undefined) {
        return `to ${value.to}`;
      }
      if (value.to === undefined && value.from !== undefined) {
        return `from ${value.from}`;
      }
      return `${value.from} — ${value.to}`;
    default:
      return '';
  }
};

const clamp = (min: number, max: number, value: number) =>
  value < min ? min : value > max ? max : value;

const formatRemaining = (type: string, limit: any, usage: any) => {
  switch (type) {
    case 'COUNT':
      return formatValue(type, clamp(0, Infinity, limit - usage));
    case 'KM':
    case 'SQUARE_KM':
      const percent = limit === 0 ? 0 : ((limit - usage) / limit) * 100;
      return `${clamp(0, 100, percent).toFixed(0)}%`;
    default:
      return '';
  }
};

const formatPercentage = (limit: any, usage: any) => {
  if (isNaN(Number(usage)) || isNaN(Number(usage))) {
    return '';
  }
  return clamp(0, 100, limit === 0 ? 100 : (usage / limit) * 100) + '%';
};

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  ${tombac.altText({ fontSize: 12, fontWeight: 'bold' })}
`;

const LimitValue = styled.div`
  flex-shrink: 0;
  ${tombac.text({ fontSize: 12, fontWeight: 500 })}
`;

const UsageInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${tombac.space(1)};
  margin-top: ${tombac.space(2)};
  ${tombac.text({ color: tombac.color('neutral', 700), fontSize: 12 })}
`;

const UsageBar = styled.div`
  background-color: ${tombac.color('neutral', 400)};
  border-radius: ${tombac.unit(3)};
  height: ${tombac.unit(6)};
  overflow: hidden;
`;

const UsageBarFill = styled.div<{ width: string }>`
  background-color: ${defaultColorPalette.brandGreen};
  height: 100%;
  width: ${({ width }) => width};
`;

export function LimitUsage(props: { limitWithUsage: LimitWithUsage }) {
  const { schema, sharedWith, limit, usage } = props.limitWithUsage;

  return schema ? (
    <StyledLimits.Popup.Item>
      <Title>
        <div>{schema.label}</div>
        <LimitValue>{formatValue(schema.type, limit)}</LimitValue>
      </Title>
      {sharedWith && (
        <Text fontSize={12} fontWeight={500} variant="secondary">
          Shared limit with {getProductName(sharedWith)}
        </Text>
      )}
      {usage !== null && usage !== undefined && (
        <>
          <UsageInfo>
            <div>{formatValue(schema.type, usage)} used</div>
            <div>{formatRemaining(schema.type, limit, usage)} available</div>
          </UsageInfo>
          <UsageBar>
            <UsageBarFill width={formatPercentage(limit, usage)} />
          </UsageBar>
        </>
      )}
    </StyledLimits.Popup.Item>
  ) : null;
}
